import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { X, Loader2, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Paid USB SKUs match the backend _SKU_BAND_RX whitelist exactly.
// We offer the SKU implied by the demo's grade band as the default,
// but the rep may change it (e.g. demo was 6-8, buyer actually wants K-12).
//
// BLOCKER-A fix (2026-02-19): extended with seven-band commercial SKUs.
// Canonical band values mirror backend ``_BAND_TO_GRADES`` keys. Legacy
// bands retained for in-flight orders already quoted on the old SKU set.
const BAND_TO_SKU = {
  // Seven-band commercial (preferred for new orders)
  'K':    'MOS-USB-K',
  '1':    'MOS-USB-G1',
  '2':    'MOS-USB-G2',
  '3-4':  'MOS-USB-34',
  '5-6':  'MOS-USB-56',
  '7-8':  'MOS-USB-78',
  '9-12': 'MOS-USB-912',
  // Legacy edition SKUs
  'K-2':  'MOS-USB-K2',
  'K-5':  'MOS-USB-K5',
  '3-5':  'MOS-USB-3-5',
  '6-8':  'MOS-USB-68',
  'K-8':  'MOS-USB-K8',
  'K-12': 'MOS-USB-K12',
};
const PAID_SKUS = Object.values(BAND_TO_SKU);

const inputStyle = {
  width: '100%', padding: '8px 10px', fontSize: 14,
  border: '1px solid #D1D5DB', background: '#fff',
};
const label = (txt) => ({
  display: 'block', fontSize: 11, fontWeight: 700,
  color: '#374151', letterSpacing: 0.4, textTransform: 'uppercase',
  marginBottom: 4,
});

const ConvertDemoModal = ({ demo, onClose, onConverted }) => {
  const defaultSku = BAND_TO_SKU[demo.initial_grade_band] || 'MOS-USB-K8';
  const ship = demo.shipping_address || {};
  const buyer = demo.buyer_contact || {};

  const [form, setForm] = useState({
    district_name:  demo.district_name === '— Demo —' ? '' : (demo.district_name || ''),
    school_name:    demo.school_name || '',
    contact_name:   buyer.name  || demo?.demo_metadata?.prospect || '',
    contact_email:  buyer.email || '',
    contact_phone:  buyer.phone || '',
    shipping_line1: ship.line1 || '',
    shipping_line2: ship.line2 || '',
    shipping_city:  ship.city  || '',
    shipping_state: ship.state || '',
    shipping_zip:   ship.zip   || '',
    sku:        defaultSku,
    quantity:   1,
    unit_price: '',
    po_number:    '',
    quote_number: '',
  });
  const [stage, setStage] = useState('form');   // form | confirm
  const [busy, setBusy]   = useState(false);
  const [err, setErr]     = useState(null);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canSubmit = useMemo(() => {
    return (
      form.district_name.trim() &&
      form.school_name.trim() &&
      form.contact_name.trim() &&
      form.sku && PAID_SKUS.includes(form.sku) &&
      Number(form.quantity) > 0 &&
      Number(form.unit_price) > 0
    );
  }, [form]);

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const token = localStorage.getItem('mosaic_session_token');
      const shipping = form.shipping_line1 ? {
        line1: form.shipping_line1,
        line2: form.shipping_line2 || null,
        city:  form.shipping_city,
        state: form.shipping_state,
        zip:   form.shipping_zip,
      } : null;
      const payload = {
        po_number:    form.po_number    || null,
        quote_number: form.quote_number || null,
        district_name: form.district_name,
        school_name:   form.school_name,
        buyer_contact: {
          name:  form.contact_name,
          email: form.contact_email || null,
          phone: form.contact_phone || null,
        },
        shipping_address: shipping,
        line_items: [{
          sku:        form.sku,
          quantity:   Number(form.quantity),
          unit_price: Number(form.unit_price),
        }],
      };
      const res = await axios.post(
        `${API}/api/usb-orders/${demo.order_id}/convert-to-order`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      onConverted?.(res.data);
      onClose?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === 'object' && detail?.converted_to_order_id) {
        setErr(`This demo was already converted to ${detail.converted_to_order_id}.`);
      } else {
        setErr(typeof detail === 'string' ? detail : (e.message || 'Conversion failed'));
      }
    } finally {
      setBusy(false);
    }
  };

  const total = (Number(form.quantity) || 0) * (Number(form.unit_price) || 0);

  return (
    <div
      data-testid="convert-demo-modal"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15, 27, 45, 0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 100, padding: 20,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', maxWidth: 760, width: '100%',
          maxHeight: '90vh', overflow: 'auto', border: '1px solid #E5E7EB',
        }}
      >
        <div style={{
          padding: '14px 20px', borderBottom: '1px solid #E5E7EB',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#92400E',
                          textTransform: 'uppercase', letterSpacing: 0.4 }}>
              Convert Demo → Paid Order
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#0F1B2D', marginTop: 2 }}>
              {demo.school_name || demo.district_name || demo.order_id}
            </div>
          </div>
          <button
            data-testid="convert-close"
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280' }}
          >
            <X size={18} />
          </button>
        </div>

        {stage === 'form' && (
          <div style={{ padding: 20 }}>
            <div style={{
              background: '#DBEAFE', border: '1px solid #93C5FD',
              padding: 10, marginBottom: 16, fontSize: 12, color: '#1E40AF',
            }}>
              A new paid order will be created using the customer and grade-band
              information from this demo. The demo record will remain unchanged
              and linked to the new order. <strong>No USB will be built or written
              until the normal fulfillment process is started.</strong>
            </div>

            <h3 style={{ fontSize: 13, color: '#374151', margin: '0 0 10px',
                         textTransform: 'uppercase', letterSpacing: 0.4 }}>
              Customer
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={label()}>District / Organization</label>
                <input data-testid="convert-district" style={inputStyle}
                       value={form.district_name}
                       onChange={(e) => upd('district_name', e.target.value)} />
              </div>
              <div>
                <label style={label()}>School / Recipient</label>
                <input data-testid="convert-school" style={inputStyle}
                       value={form.school_name}
                       onChange={(e) => upd('school_name', e.target.value)} />
              </div>
              <div>
                <label style={label()}>Contact name</label>
                <input data-testid="convert-contact-name" style={inputStyle}
                       value={form.contact_name}
                       onChange={(e) => upd('contact_name', e.target.value)} />
              </div>
              <div>
                <label style={label()}>Contact email</label>
                <input data-testid="convert-contact-email" type="email" style={inputStyle}
                       value={form.contact_email}
                       onChange={(e) => upd('contact_email', e.target.value)} />
              </div>
              <div>
                <label style={label()}>Contact phone</label>
                <input data-testid="convert-contact-phone" style={inputStyle}
                       value={form.contact_phone}
                       onChange={(e) => upd('contact_phone', e.target.value)} />
              </div>
            </div>

            <h3 style={{ fontSize: 13, color: '#374151', margin: '20px 0 10px',
                         textTransform: 'uppercase', letterSpacing: 0.4 }}>
              Shipping address
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10, marginBottom: 10 }}>
              <input data-testid="convert-ship-line1" style={inputStyle} placeholder="Street line 1"
                     value={form.shipping_line1} onChange={(e) => upd('shipping_line1', e.target.value)} />
              <input data-testid="convert-ship-line2" style={inputStyle} placeholder="Line 2 (optional)"
                     value={form.shipping_line2} onChange={(e) => upd('shipping_line2', e.target.value)} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10 }}>
              <input data-testid="convert-ship-city" style={inputStyle} placeholder="City"
                     value={form.shipping_city} onChange={(e) => upd('shipping_city', e.target.value)} />
              <input data-testid="convert-ship-state" style={inputStyle} placeholder="State"
                     value={form.shipping_state} onChange={(e) => upd('shipping_state', e.target.value)} />
              <input data-testid="convert-ship-zip" style={inputStyle} placeholder="ZIP"
                     value={form.shipping_zip} onChange={(e) => upd('shipping_zip', e.target.value)} />
            </div>

            <h3 style={{ fontSize: 13, color: '#374151', margin: '20px 0 10px',
                         textTransform: 'uppercase', letterSpacing: 0.4 }}>
              Paid line item
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10 }}>
              <div>
                <label style={label()}>Paid SKU</label>
                <select data-testid="convert-sku" style={inputStyle}
                        value={form.sku} onChange={(e) => upd('sku', e.target.value)}>
                  {PAID_SKUS.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label style={label()}>Quantity</label>
                <input data-testid="convert-qty" type="number" min="1" style={inputStyle}
                       value={form.quantity} onChange={(e) => upd('quantity', e.target.value)} />
              </div>
              <div>
                <label style={label()}>Unit price ($)</label>
                <input data-testid="convert-unit-price" type="number" min="0.01" step="0.01" style={inputStyle}
                       value={form.unit_price}
                       onChange={(e) => upd('unit_price', e.target.value)}
                       placeholder="e.g. 399.00" />
              </div>
            </div>

            <h3 style={{ fontSize: 13, color: '#374151', margin: '20px 0 10px',
                         textTransform: 'uppercase', letterSpacing: 0.4 }}>
              Commercial references (optional at creation)
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label style={label()}>PO number</label>
                <input data-testid="convert-po" style={inputStyle}
                       value={form.po_number} onChange={(e) => upd('po_number', e.target.value)}
                       placeholder="Captured here or during Authorize" />
              </div>
              <div>
                <label style={label()}>Quote number</label>
                <input data-testid="convert-quote" style={inputStyle}
                       value={form.quote_number} onChange={(e) => upd('quote_number', e.target.value)} />
              </div>
            </div>
            <div style={{ fontSize: 11, color: '#6B7280', marginTop: 6 }}>
              PO / quote can be captured here or during the normal Authorize step.
              The order will land in <code>new</code> / <code>incomplete</code> status
              and advance through the normal gates.
            </div>

            {err && <div data-testid="convert-error" style={{ marginTop: 14, color: '#991B1B', fontSize: 13 }}>{err}</div>}

            <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button data-testid="convert-cancel" onClick={onClose}
                      style={{ padding: '10px 20px', fontSize: 13, fontWeight: 700,
                               background: '#fff', color: '#374151',
                               border: '1px solid #D1D5DB', cursor: 'pointer' }}>
                Cancel
              </button>
              <button data-testid="convert-review"
                      disabled={!canSubmit}
                      onClick={() => setStage('confirm')}
                      style={{ padding: '10px 20px', fontSize: 13, fontWeight: 700,
                               background: canSubmit ? '#002FA7' : '#9CA3AF',
                               color: '#fff', border: 'none',
                               cursor: canSubmit ? 'pointer' : 'not-allowed',
                               display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                Review <ArrowRight size={14} />
              </button>
            </div>
          </div>
        )}

        {stage === 'confirm' && (
          <div style={{ padding: 20 }} data-testid="convert-confirm-pane">
            <div style={{ fontSize: 15, fontWeight: 700, color: '#0F1B2D', marginBottom: 10 }}>
              Create paid USB order from this demo?
            </div>
            <div style={{ fontSize: 13, color: '#374151', marginBottom: 16, lineHeight: 1.5 }}>
              A new paid order will be created using the customer and grade-band
              information from this demo. The demo record will remain unchanged
              and linked to the new order. No USB will be built or written until
              the normal fulfillment process is started.
            </div>
            <div style={{
              background: '#F9FAFB', border: '1px solid #E5E7EB',
              padding: 14, marginBottom: 14,
              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 13,
            }}>
              <div><strong>District:</strong> {form.district_name}</div>
              <div><strong>School:</strong> {form.school_name}</div>
              <div><strong>Contact:</strong> {form.contact_name}</div>
              <div><strong>Email:</strong> {form.contact_email || '—'}</div>
              <div><strong>Phone:</strong> {form.contact_phone || '—'}</div>
              <div><strong>Shipping:</strong> {form.shipping_line1
                ? `${form.shipping_line1}, ${form.shipping_city} ${form.shipping_state} ${form.shipping_zip}`
                : '— (will be required before build)'}</div>
              <div><strong>Paid SKU:</strong> <code>{form.sku}</code></div>
              <div><strong>Quantity:</strong> {form.quantity}</div>
              <div><strong>Unit price:</strong> ${Number(form.unit_price).toFixed(2)}</div>
              <div><strong>Total:</strong> ${total.toFixed(2)}</div>
              <div><strong>PO:</strong> {form.po_number || '— (captured at Authorize)'}</div>
              <div><strong>Quote:</strong> {form.quote_number || '—'}</div>
            </div>
            <div style={{
              background: '#FEF3C7', border: '1px solid #FDE68A',
              padding: 10, marginBottom: 14, fontSize: 12, color: '#92400E',
            }}>
              <strong>Hard guardrails:</strong> no charge, no invoice, no build,
              no USB write, no inventory reservation. The demo record stays intact.
            </div>

            {err && <div data-testid="convert-error" style={{ marginBottom: 12, color: '#991B1B', fontSize: 13 }}>{err}</div>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button data-testid="convert-back"
                      onClick={() => setStage('form')} disabled={busy}
                      style={{ padding: '10px 20px', fontSize: 13, fontWeight: 700,
                               background: '#fff', color: '#374151',
                               border: '1px solid #D1D5DB',
                               cursor: busy ? 'not-allowed' : 'pointer' }}>
                Back
              </button>
              <button data-testid="convert-submit"
                      onClick={submit} disabled={busy}
                      style={{ padding: '10px 20px', fontSize: 13, fontWeight: 700,
                               background: busy ? '#9CA3AF' : '#166534',
                               color: '#fff', border: 'none',
                               cursor: busy ? 'not-allowed' : 'pointer',
                               display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                {busy ? <Loader2 size={14} className="animate-spin" /> : null}
                Create Paid Order
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConvertDemoModal;
